import * as types from './actionTypes';
import * as constants from '../../Constants/Search';
import {
	assets3DSearchApiErrorMsg,
	productsSearchApiErrorMsg,
	assetsAndProductsSearchApiErrorMsg,
} from '../../Constants/ErrorMessages';
import axios from 'axios';
import { assetEsUrl, esApiKey, productEsUrl } from '../../Constants/Api';
import { openErrorSnackBar } from './snackBarActions';
import { logAndReturnErrorMsg } from '../../Utils/ErrorLog';
import { getProductMergedDataFromElasticSearch } from './manageProductActions';
import { assetEsSearchFields } from '../../Constants/Asset';
import { productElasticSearchMatchFields } from '../../Constants/Products';

export function dispatchActionData(type, data) {
	return { type: type, data: data };
}

export function setSelectionOption(data) {
	return function (dispatch) {
		dispatch(dispatchActionData(types.SET_SELECTEDSEARCH_OPTION, data));
	};
}

export function setSearchTerm(searchTerm) {
	return function (dispatch) {
		dispatch(dispatchActionData(types.SET_SEARCH_TERM, searchTerm));
	};
}
export function resetSearchTerm() {
	return function (dispatch) {
		dispatch(dispatchActionData(types.RESET_SEARCH_SEARCHTERM));
	};
}
//Action method to set the category selection for the search
export function setSearchCategory(selectedCategoryValue) {
	return function (dispatch) {
		dispatch(dispatchActionData(types.SET_SEARCH_CATEGORY, selectedCategoryValue));
	};
}
//Resetting action when searchTerm is empty
export function resetSearchOptions() {
	return { type: types.RESET_SEARCH_DATA };
}

//gets data from the requested url and returns a promise
export async function get3dAssets(searchTerm, size, from, isBuyable, isArchived, tags) {
	try {
		let assets = await getAssetsFromES(searchTerm, size, from, isBuyable, isArchived, tags);
		if (assets) {
			return assets;
		}
		return {
			count: 0,
			data: [],
		};
	} catch (error) {
		throw error;
	}
}

// below method retrieves assets from ES asset index. with no filters for dropdown search.
export const getESAssetsNoFilters = (searchQuery) => {
	const query = {
		size: 100,
		query: {
			bool: {
				must: {
					multi_match: {
						query: `${searchQuery}`,
						operator: 'AND',
						fields: assetEsSearchFields,
					},
				},
			},
		},
	};
	return axios
		.get(`${assetEsUrl}/_search`, {
			headers: {
				Authorization: `ApiKey ${esApiKey}`,
			},
			params: {
				source: JSON.stringify(query),
				source_content_type: 'application/json',
			},
		})
		.then((response) => {
			let assets = response?.data?.hits?.hits.map((a) => a?._source);
			// Adding type prop to response data objects.
			// it is used for grouping together results in autocomplete suggestions
			// displaying info on respective tabs
			assets = assets.map((obj) => ({
				...obj,
				type: constants.assets3dGroupByType,
				tabType: constants.assets3dTabType,
			}));
			if (assets.length > 0) {
				return assets;
			}
			return [];
		})
		.catch((error) => {
			throw error;
		});
};

// below method retrieves merged product data from pcm and bifrost from `Product` index of ES. for search  autocomplete.
export const getESProdNoFilters = (searchQuery) => {
	const query = {
		size: 100,
		query: {
			bool: {
				must: {
					multi_match: {
						query: `${searchQuery}`,
						operator: 'AND',
						fields: productElasticSearchMatchFields,
					},
				},
			},
		},
	};
	return axios
		.get(`${productEsUrl}/_search`, {
			headers: {
				Authorization: `ApiKey ${esApiKey}`,
			},
			params: {
				source: JSON.stringify(query),
				source_content_type: 'application/json',
			},
		})
		.then((response) => {
			if (response?.data?.hits?.hits) {
				const pcmData = response.data.hits.hits;
				// Adding type prop to response data objects.
				// it is used for grouping together results in autocomplete suggestions
				// displaying info on respective tabs
				response.data = pcmData.map((obj) => {
					// TODO: remove _id when id is available from product index in product object.
					obj._source.product.id = obj._id;
					return {
						// get the _source object from ES results
						...obj._source,
						type: constants.productsGroupByType,
						tabType: constants.productsTabType,
					};
				});
			}
			if (response.data.length > 0) {
				return response.data;
			} else {
				return [];
			}
		})
		.catch((error) => {
			throw error;
		});
};
// below method retrieves assets from ES asset index. with default filters for results page.
export const getAssetsFromES = (searchQuery, size = 100, from = 0, isBuyable = true, isArchived = false, tags = []) => {
	const must = [];
	must.push({
		multi_match: {
			query: isBuyable ? 'Y' : 'N',
			fields: ['product.isBuyable'],
		},
	});
	must.push({
		multi_match: {
			query: isArchived,
			fields: ['archived'],
		},
	});
	if (tags.length > 0) {
		must.push({
			multi_match: {
				query: tags.join(),
				fields: ['tags'],
			},
		});
	}

	const query = {
		size: size,
		from: from,
		track_total_hits: true,
		query: {
			bool: {
				must: {
					multi_match: {
						query: `${searchQuery}`,
						operator: 'AND',
						fields: assetEsSearchFields,
					},
				},
				filter: {
					bool: {
						must: must,
					},
				},
			},
		},
	};
	return axios
		.get(`${assetEsUrl}/_search`, {
			headers: {
				Authorization: `ApiKey ${esApiKey}`,
			},
			params: {
				source: JSON.stringify(query),
				source_content_type: 'application/json',
			},
		})
		.then((response) => {
			const totalHits = response.data.hits.total.value;
			let assets = response?.data?.hits?.hits.map((a) => a?._source);
			// Adding type prop to response data objects.
			// it is used for grouping together results in autocomplete suggestions
			// displaying info on respective tabs
			assets = assets.map((obj) => ({
				...obj,
				type: constants.assets3dGroupByType,
				tabType: constants.assets3dTabType,
			}));
			return {
				count: totalHits,
				data: assets,
			};
		})
		.catch((error) => {
			throw error;
		});
};
// Get data from the products end point and returns a promise
export function getProducts(searchQuery, size, from, isBuyable) {
	return getProductMergedDataFromElasticSearch(searchQuery, size, from, isBuyable)
		.then((response) => {
			if (response?.data?.hits?.hits) {
				const pcmData = response.data.hits.hits;
				const totalHits = response.data.hits.total.value;
				// Adding type prop to response data objects.
				// it is used for grouping together results in autocomplete suggestions
				// displaying info on respective tabs
				response.data = pcmData.map((obj) => {
					// TODO: remove _id when id is available from product index in product object.
					obj._source.product.id = obj._id;
					return {
						// get the _source object from ES results
						...obj._source,
						type: constants.productsGroupByType,
						tabType: constants.productsTabType,
					};
				});
				return {
					count: totalHits,
					data: response.data,
				};
			} else {
				return {
					count: 0,
					data: [],
				};
			}
		})
		.catch((error) => {
			throw error;
		});
}

//Get data for both 3d Assets and products
export function get3dAssetsAndProductsData(data) {
	return Promise.all([getESAssetsNoFilters(data), getESProdNoFilters(data)])
		.then(function (response) {
			return response;
		})
		.catch((error) => {
			throw error;
		});
}

export function loadSearchData(searchQuery, selectedCategoryValue) {
	return function (dispatch) {
		let results = [];
		// making API calls basing on the category selection and populating redux store with the information.
		if (selectedCategoryValue === constants.assets3dVal) {
			return getESAssetsNoFilters(searchQuery)
				.then((response) => {
					if (response) {
						results = results.concat(response);
						dispatch(dispatchActionData(types.LOAD_SEARCH_AUTOCOMPLETE_DATA, results));
						return response;
					}
				})
				.catch((error) => {
					dispatch(openErrorSnackBar(logAndReturnErrorMsg(error, assets3DSearchApiErrorMsg)));
				});
		} else if (selectedCategoryValue === constants.productVal) {
			return getESProdNoFilters(searchQuery)
				.then((response) => {
					if (response) {
						results = results.concat(response);
						dispatch(dispatchActionData(types.LOAD_SEARCH_AUTOCOMPLETE_DATA, results));
						return response;
					}
				})
				.catch((error) => {
					dispatch(openErrorSnackBar(logAndReturnErrorMsg(error, productsSearchApiErrorMsg)));
				});
		} else if (selectedCategoryValue === constants.allVal) {
			// chaining both the api calls and aggregating the results.
			return get3dAssetsAndProductsData(searchQuery)
				.then(function (response) {
					if (response[0] && response[1]) {
						results = results.concat(response[0]);
						results = results.concat(response[1]);
						dispatch(dispatchActionData(types.LOAD_SEARCH_AUTOCOMPLETE_DATA, results));
					}
				})
				.catch(function (error) {
					dispatch(openErrorSnackBar(logAndReturnErrorMsg(error, assetsAndProductsSearchApiErrorMsg)));
				});
		}
	};
}
